// Interns — People & Org admin tab. Interns are remote-only employees
// (employmentType "intern"): exempt from the office-WiFi check-in gate, normal
// 10:00 on-time/late rules, and can apply for leave. Added the same way as
// staff (reuses POST /admin/users with employmentType "intern"); listed via
// GET /hr/staff?employmentType=intern (interns are excluded from the Staff tab).

import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Flex, Heading, Text, Button, HStack, Badge, Spinner, useToast,
  Table, Thead, Tbody, Tr, Th, Td, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
  FormControl, FormLabel, Input, Select, SimpleGrid, IconButton, Tooltip,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, RepeatIcon } from "@chakra-ui/icons";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";

const EMPTY = { username: "", email: "", password: "", team: "", designation: "", phone: "" };

export default function InternsAdminPage() {
  useShellHeader({ title: "Interns", breadcrumb: "People & Org · Interns" }, []);
  const toast = useToast();
  const modal = useDisclosure();
  const [interns, setInterns] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api("/hr/staff?employmentType=intern"),
        api("/admin/teams").catch(() => ({ teams: [] })),
      ]);
      setInterns(s.staff || []);
      setTeams(t.teams || []);
    } catch (e) {
      toast({ title: e.message || "Could not load interns", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  function openAdd() { setF(EMPTY); modal.onOpen(); }

  async function saveIntern() {
    if (!f.username || !f.email || !f.password) {
      toast({ title: "Username, email and password are required", status: "warning" });
      return;
    }
    setSaving(true);
    try {
      await api("/admin/users", {
        method: "POST",
        body: JSON.stringify({ ...f, role: "TEAM_USER", employmentType: "intern" }),
      });
      toast({ title: "Intern added", status: "success" });
      modal.onClose();
      await load();
    } catch (e) {
      toast({ title: e.message || "Could not add intern", status: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function removeIntern(u) {
    if (!window.confirm(`Remove intern "${u.username}" (${u.email})?`)) return;
    setBusyId(u._id);
    try {
      await api(`/admin/users/${u._id}`, { method: "DELETE" });
      toast({ title: "Intern removed", status: "success" });
      setInterns((prev) => prev.filter((x) => x._id !== u._id));
    } catch (e) {
      toast({ title: e.message || "Could not remove", status: "error" });
    } finally {
      setBusyId(null);
    }
  }

  const teamName = (u) => (u.team && (u.team.name || "")) || "—";
  const displayName = (u) => u.username || `${u.firstName || ""} ${u.lastName || ""}`.trim() || "—";

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
        <Box>
          <Heading size="md">Interns</Heading>
          <Text fontSize="sm" color="gray.500">
            Remote-only interns — they check in from any network (exempt from the office-WiFi rule),
            with the usual 10:00 on-time / late rules, and can apply for leave.
          </Text>
        </Box>
        <HStack>
          <Badge colorScheme="purple" px={2} py={1} rounded="md">{interns.length} intern{interns.length === 1 ? "" : "s"}</Badge>
          <Tooltip label="Refresh"><IconButton aria-label="Refresh" icon={<RepeatIcon />} size="sm" variant="ghost" onClick={load} isLoading={loading} /></Tooltip>
          <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openAdd}>Add Intern</Button>
        </HStack>
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}><Spinner /></Flex>
      ) : interns.length === 0 ? (
        <Box py={10} textAlign="center" color="gray.500">No interns yet. Click “Add Intern” to create one.</Box>
      ) : (
        <Box overflowX="auto" borderWidth="1px" rounded="lg">
          <Table size="sm">
            <Thead>
              <Tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Team</Th><Th>Designation</Th><Th textAlign="right">Action</Th></Tr>
            </Thead>
            <Tbody>
              {interns.map((u) => (
                <Tr key={u._id}>
                  <Td fontWeight={600}>{displayName(u)}</Td>
                  <Td>{u.email}</Td>
                  <Td>{u.phone || "—"}</Td>
                  <Td>{teamName(u)}</Td>
                  <Td>{u.designation || "—"}</Td>
                  <Td textAlign="right">
                    <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<DeleteIcon />} isLoading={busyId === u._id} onClick={() => removeIntern(u)}>Remove</Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Intern</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={2} spacing={3}>
              <FormControl isRequired><FormLabel>Username</FormLabel><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></FormControl>
              <FormControl isRequired><FormLabel>Email</FormLabel><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></FormControl>
              <FormControl isRequired><FormLabel>Password</FormLabel><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Team</FormLabel>
                <Select placeholder="No team" value={f.team} onChange={(e) => setF({ ...f, team: e.target.value })}>
                  {teams.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
                </Select>
              </FormControl>
              <FormControl><FormLabel>Designation</FormLabel><Input value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Phone</FormLabel><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></FormControl>
            </SimpleGrid>
            <Text fontSize="xs" color="gray.500" mt={3}>Created as a remote employee (employment type “intern”). Share the password so they can log in.</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={modal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={saveIntern} isLoading={saving}>Create Intern</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
